// A creature's AC and HP on its initiative row, read from its Roll20 token bars.
//
// Owner, 2026-10-08: "monster HP and AC from Roll20 bars, eventually with a dynamically
// updated crosswalk for hp and AC adjustments on the fly." The bars are the source of
// truth (the tracker has never stored a monster's HP), so this only READS them and says
// plainly when a bar holds no number. Which bar is HP and which is AC comes from the
// DM's own NPC bar layout (Roll20 options), never a hard-coded bar 3.
import type { MapToken } from "./auraReach";
import { barFor, npcBarLayout, type BarNo } from "./tokenBars";
import { normName as norm } from "./text";
import type { Layout } from "./types";

export interface Vitals { tokenId: string; name: string; hp: number | null; hpMax: number | null; ac: number | null; hpBar: BarNo | null }

const num = (v: unknown): number | null => {
  const m = String(v ?? "").trim().match(/^-?\d+/);
  return m ? Number(m[0]) : null;
};
const rawOf = (t: MapToken, n: BarNo): { value: string; max: string } => {
  const bars = (t as unknown as { bars?: Record<string, { value?: string; max?: string }> }).bars || {};
  const b = bars[`bar${n}`];
  const own = n === 1 ? t.bar1 : n === 2 ? t.bar2 : n === 3 ? t.bar3 : t.bar4;
  return { value: String(b?.value ?? own ?? ""), max: String(b?.max ?? "") };
};

/** Reads one token. A blank or text bar is null, never 0: an unset HP bar is not a dead creature. */
export function vitalsOf(t: MapToken, layout: Layout): Vitals {
  const bars = npcBarLayout(layout);
  const hpBar = barFor(bars, "hp"), acBar = barFor(bars, "ac");
  const hp = hpBar ? rawOf(t, hpBar) : null;
  const ac = acBar ? rawOf(t, acBar) : null;
  return { tokenId: t.id, name: t.name, hp: hp ? num(hp.value) : null, hpMax: hp ? num(hp.max) : null, ac: ac ? num(ac.value) : null, hpBar };
}

/** The token that is this row: the id Roll20 gave it, else the exact (normalised) name.
 *  Never a first-word match: "Goblin" must not claim "Goblin Boss". */
export function tokenFor<T extends { id: string; name: string }>(tokens: T[], entry: { tokenId?: string; name: string }): T | undefined {
  if (entry.tokenId && entry.tokenId !== "-1") { const byId = tokens.find((t) => t.id === entry.tokenId); if (byId) return byId; }
  const wanted = norm(entry.name);
  if (!wanted) return undefined;
  // Three tokens called "Goblin" are three creatures: binding a row to the first would show, and WRITE, the wrong one.
  const same = tokens.filter((t) => norm(t.name) === wanted);
  return same.length === 1 ? same[0] : undefined;
}

/** A compact fingerprint, so a poll that changed nothing re-renders nothing. */
export const vitalsSignature = (list: Vitals[]) => list.map((v) => `${v.tokenId}:${v.hp}/${v.hpMax}:${v.ac}`).join("|");

export type HpState = "down" | "bloodied" | "ok" | "unknown";
export function hpState(v: Vitals): HpState {
  if (v.hp === null) return "unknown";
  if (v.hp <= 0) return "down";
  return v.hpMax !== null && v.hpMax > 0 && v.hp <= v.hpMax / 2 ? "bloodied" : "ok";
}
